import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAppSettings, type Plan } from "@/hooks/useAppSettings";
import { useSubscription } from "@/hooks/useSubscription";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Check, Zap, Loader2, Settings as SettingsIcon, ArrowUpRight, ArrowDownRight, CreditCard, Receipt, Ban, Building2, Clock } from "lucide-react";
import { toast } from "sonner";
import { getPaddleEnvironment } from "@/lib/paddle";
import { BankTransferDialog } from "@/components/BankTransferDialog";

const PLAN_RANK: Record<string, number> = { free: 0, plus: 1, pro: 2 };

export default function Settings() {
  const { user } = useAuth();
  const { plans } = useAppSettings();
  const { subscription, isActive, loading: subLoading, refetch } = useSubscription();
  const { openCheckout, loading: checkoutLoading } = usePaddleCheckout();
  const [searchParams, setSearchParams] = useSearchParams();
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [bankDialog, setBankDialog] = useState<{ open: boolean; plan: Plan | null }>({ open: false, plan: null });
  const [pendingPayment, setPendingPayment] = useState<any>(null);

  // Resolve current plan from active subscription, fallback to free.
  const currentPlanId = useMemo(() => {
    if (isActive && subscription) {
      if (subscription.product_id === "pro_plan") return "pro";
      if (subscription.product_id === "plus_plan") return "plus";
    }
    return "free";
  }, [isActive, subscription]);

  useEffect(() => {
    const status = searchParams.get("checkout");
    if (status === "success") {
      toast.success("Payment successful!", { description: "Your plan is being activated." });
      setSearchParams({});
      // Webhook may take a couple seconds - refetch a few times.
      [1500, 4000, 8000].forEach((ms) => setTimeout(refetch, ms));
    } else if (status === "cancelled") {
      toast.info("Checkout cancelled");
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Watch for pending bank transfer payments
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("manual_payments")
        .select("*")
        .eq("user_id", user.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      setPendingPayment(data);
    })();
  }, [user, bankDialog.open]);

  const handlePlanAction = async (target: Plan) => {
    if (!user) {
      toast.error("Please sign in first");
      return;
    }
    if (!target.priceId) {
      toast.error("This plan is not available for purchase");
      return;
    }

    const fromRank = PLAN_RANK[currentPlanId] ?? 0;
    const toRank = PLAN_RANK[target.id] ?? 0;

    // No active paid subscription, OR upgrading from a paid plan → open checkout
    const isUpgrade = toRank > fromRank;
    if (!isActive || currentPlanId === "free" || isUpgrade) {
      try {
        await openCheckout({
          priceId: target.priceId,
          customerEmail: user.email ?? undefined,
          userId: user.id,
        });
      } catch (e) {
        toast.error("Could not open checkout", {
          description: e instanceof Error ? e.message : "Unknown error",
        });
      }
      return;
    }

    // Downgrade → schedule via change-plan (takes effect at next renewal)
    try {
      const { data, error } = await supabase.functions.invoke("change-plan", {
        body: {
          targetPriceId: target.priceId,
          mode: "downgrade",
          environment: getPaddleEnvironment(),
        },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      toast.success("Plan change scheduled for next renewal");
      [1500, 4000].forEach((ms) => setTimeout(refetch, ms));
    } catch (e) {
      toast.error("Could not change plan", {
        description: e instanceof Error ? e.message : "Unknown error",
      });
    }
  };

  const openPortal = async () => {
    setPortalOpen(true);
    setPortalUrl(null);
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { environment: getPaddleEnvironment() },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      if (!data?.url) throw new Error("No portal URL returned");
      setPortalUrl(data.url);
    } catch (e) {
      setPortalOpen(false);
      toast.error("Could not open billing portal", {
        description: e instanceof Error ? e.message : "Unknown error",
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const closePortal = () => {
    setPortalOpen(false);
    setPortalUrl(null);
    // Refresh subscription state in case the user made changes.
    [500, 2500].forEach((ms) => setTimeout(refetch, ms));
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-subscription", {
        body: { environment: getPaddleEnvironment() },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      const endDate = data?.currentPeriodEnd ? new Date(data.currentPeriodEnd).toLocaleDateString() : null;
      toast.success("Subscription cancellation scheduled", {
        description: endDate ? `You keep access until ${endDate}.` : "You keep access until your current period ends.",
      });
      setCancelDialogOpen(false);
      closePortal();
      [1000, 3000, 7000].forEach((ms) => setTimeout(refetch, ms));
    } catch (e) {
      toast.error("Could not cancel subscription", {
        description: e instanceof Error ? e.message : "Unknown error",
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const planLabel = currentPlanId.toUpperCase();
  const cancelledNotice =
    subscription?.status === "canceled" && subscription.current_period_end
      ? `Cancels on ${new Date(subscription.current_period_end).toLocaleDateString()}`
      : null;
  const pastDueNotice = subscription?.status === "past_due";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your account and subscription.</p>
      </div>

      <Card className="bg-gradient-card border-border/50">
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Row label="Email" value={user?.email ?? "-"} />
          <Row
            label="Plan"
            value={
              <div className="flex items-center gap-2">
                {subLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Badge
                    className={currentPlanId !== "free" ? "bg-gradient-primary border-0" : ""}
                    variant={currentPlanId !== "free" ? "default" : "secondary"}
                  >
                    {planLabel}
                  </Badge>
                )}
                {cancelledNotice && (
                  <span className="text-xs text-muted-foreground">{cancelledNotice}</span>
                )}
                {pastDueNotice && (
                  <Badge variant="destructive" className="text-[10px]">Payment past due</Badge>
                )}
              </div>
            }
          />
          {isActive && subscription?.environment !== "manual" && (
            <div className="pt-3">
              <Button variant="outline" size="sm" onClick={openPortal}>
                <SettingsIcon className="h-4 w-4 mr-2" />
                Manage subscription
              </Button>
            </div>
          )}
          {isActive && subscription?.environment === "manual" && (
            <p className="pt-3 text-xs text-muted-foreground">
              This plan was activated via bank transfer. To make changes, contact{" "}
              <a href="mailto:storeseoai@gmail.com" className="underline font-medium text-foreground">
                storeseoai@gmail.com
              </a>
              .
            </p>
          )}
        </CardContent>
      </Card>

      {pendingPayment && (
        <Card className="border-orange-300 bg-orange-50/50 dark:bg-orange-950/20">
          <CardContent className="py-4 flex items-start gap-3">
            <Clock className="h-5 w-5 text-orange-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">Bank transfer payment under review</p>
              <p className="text-xs text-muted-foreground">
                Submitted {new Date(pendingPayment.created_at).toLocaleString()} for the {pendingPayment.plan_id.toUpperCase()} plan (${pendingPayment.amount}). Your plan will activate once an admin approves the receipt.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-xl font-semibold mb-1">Plans</h2>
        <p className="text-sm text-muted-foreground mb-4">
          {isActive
            ? "Switch plans anytime. Upgrades are prorated; downgrades take effect at next renewal."
            : "Pick a plan to get started. Pay with card or bank transfer."}
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((p) => {
            const isCurrent = currentPlanId === p.id;
            const isPaid = p.id !== "free";
            const isManual = isActive && subscription?.environment === "manual";
            const fromRank = PLAN_RANK[currentPlanId] ?? 0;
            const toRank = PLAN_RANK[p.id] ?? 0;
            const isUpgrade = toRank > fromRank;
            const isDowngrade = toRank < fromRank && isPaid;

            let cta: React.ReactNode = null;
            if (subLoading) {
              cta = (
                <Button disabled variant="outline" className="w-full">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading…
                </Button>
              );
            } else if (isCurrent) {
              cta = <Button disabled variant="outline" className="w-full">Current plan</Button>;
            } else if (isPaid) {
              // Downgrades:
              //  - Manual (bank-transfer) plans expire on their own at period end → no button.
              //  - Paddle plans can't be downgraded directly → offer cancel instead.
              if (isDowngrade) {
                if (isManual) {
                  cta = null;
                } else {
                  cta = (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setCancelDialogOpen(true)}
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      Cancel current plan
                    </Button>
                  );
                }
              } else {
                // Upgrade or first purchase — always offer both card and bank transfer.
                const Icon = !isActive ? Zap : ArrowUpRight;
                const amountNum = Number(String(p.price).replace(/[^0-9.]/g, "")) || 0;
                cta = (
                  <div className="space-y-2">
                    <Button
                      onClick={() => handlePlanAction(p)}
                      disabled={checkoutLoading}
                      className={`w-full ${p.highlight ? "bg-gradient-primary shadow-glow" : ""}`}
                    >
                      {checkoutLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Icon className="h-4 w-4 mr-2" />
                      )}
                      Upgrade to {p.name} (Card)
                    </Button>
                    {amountNum > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => setBankDialog({ open: true, plan: p })}
                      >
                        <Building2 className="h-4 w-4 mr-2" />
                        Pay via Bank Transfer
                      </Button>
                    )}
                  </div>
                );
              }
            }

            return (
              <PlanCard
                key={p.id}
                name={p.name}
                price={p.price}
                period={p.period}
                features={p.features}
                highlight={p.highlight}
                cta={cta}
              />
            );
          })}
        </div>
      </div>

      <Dialog open={portalOpen} onOpenChange={(o) => (o ? setPortalOpen(true) : closePortal())}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage subscription</DialogTitle>
            <DialogDescription>
              Manage billing, invoices, and cancellation from one place.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/30 p-4 flex items-start gap-3">
              <CreditCard className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Payment method and invoices</p>
                <p className="text-sm text-muted-foreground">Open the secure billing portal in this window.</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start"
              disabled={!portalUrl || portalLoading}
              onClick={() => portalUrl && window.location.assign(portalUrl)}
            >
              {portalLoading || !portalUrl ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Receipt className="h-4 w-4 mr-2" />
              )}
              {portalLoading || !portalUrl ? "Preparing billing portal…" : "Manage payment and invoices"}
            </Button>
            <Button
              variant="destructive"
              className="w-full justify-start"
              disabled={cancelLoading || subscription?.status === "canceled" || subscription?.cancel_at_period_end}
              onClick={() => setCancelDialogOpen(true)}
            >
              {cancelLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
              {subscription?.status === "canceled" || subscription?.cancel_at_period_end ? "Cancellation scheduled" : "Cancel subscription"}
            </Button>
          </div>
          {(subscription?.status === "canceled" || subscription?.cancel_at_period_end) && subscription.current_period_end && (
            <p className="text-sm text-muted-foreground">
              Your plan stays active until {new Date(subscription.current_period_end).toLocaleDateString()}.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closePortal}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              Your plan will stay active until the end of the current billing period, then move to Free.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelLoading}>Keep subscription</AlertDialogCancel>
            <AlertDialogAction disabled={cancelLoading} onClick={(event) => { event.preventDefault(); handleCancelSubscription(); }}>
              {cancelLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm cancellation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {bankDialog.plan && (
        <BankTransferDialog
          open={bankDialog.open}
          onOpenChange={(o) => setBankDialog({ open: o, plan: o ? bankDialog.plan : null })}
          planId={bankDialog.plan.id}
          planName={bankDialog.plan.name}
          amount={Number(String(bankDialog.plan.price).replace(/[^0-9.]/g, "")) || 0}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function PlanCard({ name, price, period, features, highlight, cta }: {
  name: string; price: string; period: string; features: string[]; highlight?: boolean; cta: React.ReactNode;
}) {
  return (
    <Card className={`relative flex flex-col ${highlight ? "border-primary shadow-glow bg-gradient-card" : "bg-gradient-card border-border/50"}`}>
      {highlight && <Badge className="absolute -top-2 right-4 bg-gradient-primary border-0">Recommended</Badge>}
      <CardHeader>
        <CardTitle className="flex items-baseline gap-2">{name}</CardTitle>
        <CardDescription className="text-2xl font-bold text-foreground">
          {price}
          <span className="text-sm font-normal text-muted-foreground">{period}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-4">
        <ul className="space-y-2">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-success mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        {cta && <div className="mt-auto pt-2">{cta}</div>}
      </CardContent>
    </Card>
  );
}
